import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Cpu, Tags, Plus, Edit, Trash2, X, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/helpers';

const AdminPcBuilderConfig = () => {
    const [activeTab, setActiveTab] = useState('components'); 
    const [loading, setLoading] = useState(false);

    const [components, setComponents] = useState([]);
    const [tags, setTags] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [jumpPage, setJumpPage] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTab === 'components') {
                const res = await adminStaffService.getAllPcComponents();
                setComponents(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
            } else {
                const res = await adminStaffService.getAllCompatibilityGroups();
                setTags(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
            }
        } catch (error) {
            toast.error("Lỗi khi tải dữ liệu!");
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => { 
        fetchData(); 
    }, [fetchData]);

    // Chuyển Tab sẽ reset page
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setJumpPage('');
    };

    const handleUploadIcon = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Tạo FormData để chứa file (kiểm tra lại Backend của bạn xem key là 'file' hay 'upload')
        const uploadData = new FormData();
        uploadData.append('file', file); 

        setIsUploadingIcon(true);
        try {
            // Gọi API upload ảnh sẵn có của bạn
            const res = await adminStaffService.uploadEditorImage(uploadData);
            
            // Lấy URL ảnh trả về từ Server (Tùy cấu trúc API của bạn trả về, thường là res.data hoặc res.data.url)
            const imageUrl = typeof res.data === 'string' ? res.data : (res.data?.url || res.url);
            
            // Cập nhật link ảnh vào formData
            setFormData(prev => ({ ...prev, icon: imageUrl }));
            toast.success("Tải ảnh lên thành công!");
        } catch (error) {
            toast.error("Lỗi khi tải ảnh lên server!");
        } finally {
            setIsUploadingIcon(false);
        }
    };

    // --- Data Processing cho Phân Trang ---
    // Components Data
    const sortedComponents = useMemo(() => {
        return [...components].sort((a,b) => a.sortOrder - b.sortOrder);
    }, [components]);

    // Tags Data (Group theo type)
    const groupedTagsArray = useMemo(() => {
        const grouped = tags.reduce((acc, tag) => {
            if (!acc[tag.groupType]) acc[tag.groupType] = [];
            acc[tag.groupType].push(tag);
            return acc;
        }, {});
        return Object.entries(grouped);
    }, [tags]);

    // Tính toán Pagination
    const currentDataList = activeTab === 'components' ? sortedComponents : groupedTagsArray;
    const totalPages = Math.ceil(currentDataList.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(currentPage, totalPages);
    const paginatedData = currentDataList.slice((validCurrentPage - 1) * itemsPerPage, validCurrentPage * itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleJumpPage = () => {
        const page = parseInt(jumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpPage('');
        } else {
            setJumpPage('');
        }
    };

    const openModal = (item = null) => {
        setEditingItem(item);
        if (activeTab === 'components') {
            // 👉 ĐÃ SỬA: Thêm icon: '' vào form mặc định
            setFormData(item ? { ...item } : { componentName: '', componentCode: '', sortOrder: 1, icon: '' });
        } else {
            setFormData(item ? { ...item } : { groupType: '', groupValue: '' });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (activeTab === 'components') {
                if (editingItem) await adminStaffService.updatePcComponent(editingItem.componentId, formData);
                else await adminStaffService.createPcComponent(formData);
            } else {
                const payload = { ...formData, groupType: formData.groupType.toUpperCase().trim() };
                if (editingItem) await adminStaffService.updateCompatibilityGroup(editingItem.compatibilityId, payload);
                else await adminStaffService.createCompatibilityGroup(payload);
            }
            toast.success("Lưu thành công!");
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data || "Có lỗi xảy ra khi lưu!");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Bạn có chắc chắn muốn xóa?")) return;
        try {
            if (activeTab === 'components') await adminStaffService.deletePcComponent(id);
            else await adminStaffService.deleteCompatibilityGroup(id);
            toast.success("Xóa thành công!");
            fetchData();
        } catch (error) {
            toast.error("Không thể xóa vì dữ liệu đang được sử dụng!");
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Cpu size={26} className="text-tnc-orange" /> Cấu hình Build PC
            </h2>

            <div className="flex gap-4 border-b border-gray-200 mb-6">
                <button 
                    onClick={() => handleTabChange('components')}
                    className={`py-3 px-4 font-bold border-b-2 transition-colors ${activeTab === 'components' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Vị trí linh kiện (Slots)
                </button>
                <button 
                    onClick={() => handleTabChange('tags')}
                    className={`py-3 px-4 font-bold border-b-2 transition-colors ${activeTab === 'tags' ? 'border-tnc-orange text-tnc-orange' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Tags Tương thích (Compatibility)
                </button>
            </div>

            <div className="flex justify-end mb-4">
                <button onClick={() => openModal()} className="bg-tnc-orange text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-tnc-orange-hover transition-colors">
                    <Plus size={18} /> Thêm mới
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col">
                <div className="p-6">
                    {loading ? (
                        <div className="text-center py-10 text-gray-500">Đang tải dữ liệu...</div>
                    ) : activeTab === 'components' ? (
                        <div className="overflow-x-auto min-h-[400px]">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-700 border-b">
                                        <th className="p-3">Thứ tự</th>
                                        <th className="p-3">Icon</th> {/* 👉 ĐÃ THÊM */}
                                        <th className="p-3">Mã Code (Định danh)</th>
                                        <th className="p-3">Tên hiển thị</th>
                                        <th className="p-3 text-right">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.map(comp => (
                                        <tr key={comp.componentId} className="border-b hover:bg-gray-50">
                                            <td className="p-3 font-bold">{comp.sortOrder}</td>
                                            
                                            {/* 👉 ĐÃ THÊM: Hiển thị Icon. Nếu bạn dùng link ảnh thì dùng thẻ img, 
                                                nếu chỉ là text thì render bình thường */}
                                            <td className="p-3">
                                                {comp.icon ? (
                                                    <img src={getImageUrl(comp.icon)} alt={comp.componentName} className="w-8 h-8 object-contain" />
                                                ) : (
                                                    <span className="text-gray-400 text-sm">Chưa có</span>
                                                )}
                                            </td>
                                            
                                            <td className="p-3 text-tnc-orange font-mono font-bold">{comp.componentCode}</td>
                                            <td className="p-3 font-medium">{comp.componentName}</td>
                                            <td className="p-3 text-right flex justify-end gap-2">
                                                <button onClick={() => openModal(comp)} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"><Edit size={16}/></button>
                                                <button onClick={() => handleDelete(comp.componentId)} className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100"><Trash2 size={16}/></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="space-y-6 min-h-[400px]">
                            {paginatedData.length === 0 && <div className="text-center py-10 text-gray-500">Chưa có Tag tương thích nào.</div>}
                            {paginatedData.map(([type, items]) => (
                                <div key={type} className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-2 font-bold text-gray-800 border-b border-gray-200 flex items-center gap-2">
                                        <Tags size={16} className="text-tnc-orange" /> NHÓM: {type}
                                    </div>
                                    <div className="p-4 flex flex-wrap gap-3">
                                        {items.map(tag => (
                                            <div key={tag.compatibilityId} className="flex items-center gap-2 bg-white border border-gray-300 rounded-full px-3 py-1 shadow-sm">
                                                <span className="text-sm font-medium text-gray-700">{tag.groupValue}</span>
                                                <button onClick={() => openModal(tag)} className="text-blue-500 hover:text-blue-700"><Edit size={14}/></button>
                                                <button onClick={() => handleDelete(tag.compatibilityId)} className="text-red-500 hover:text-red-700"><Trash2 size={14}/></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* --- Phân trang PC Builder --- */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-wrap justify-center items-center gap-4 p-4 border-t border-gray-200 bg-gray-50/50 rounded-b-xl">
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => handlePageChange(validCurrentPage - 1)} 
                                disabled={validCurrentPage === 1}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                                {[...Array(totalPages)].map((_, i) => {
                                    const page = i + 1;
                                    return (
                                        <button 
                                            key={page} 
                                            onClick={() => handlePageChange(page)}
                                            className={`w-8 h-8 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                ${validCurrentPage === page 
                                                    ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    )
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(validCurrentPage + 1)} 
                                disabled={validCurrentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 border-l border-gray-300 pl-4">
                            <span className="text-sm text-gray-600 font-medium">Đến trang:</span>
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                value={jumpPage}
                                onChange={(e) => setJumpPage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleJumpPage()}
                                placeholder={validCurrentPage.toString()}
                                className="w-14 h-8 px-2 text-center border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 shadow-sm"
                            />
                            <button
                                onClick={handleJumpPage}
                                className="px-3 h-8 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors shadow-sm"
                            >
                                Đi
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="flex justify-between items-center bg-[#002855] p-4 text-white">
                            <h3 className="font-bold">{editingItem ? 'Sửa thông tin' : 'Thêm mới'}</h3>
                            <button onClick={() => setIsModalOpen(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {activeTab === 'components' ? (
                                <>
                                    {editingItem && (
                                        <div className="bg-yellow-50 text-yellow-800 p-3 rounded text-xs flex gap-2 items-start border border-yellow-200">
                                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                            <p>Cẩn thận: Đổi mã code có thể ảnh hưởng đến logic Build PC đang hoạt động dựa trên mã này.</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Mã Code (VD: CPU, MAIN)</label>
                                        <input type="text" required value={formData.componentCode} onChange={e => setFormData({...formData, componentCode: e.target.value.toUpperCase()})} className="w-full border p-2 rounded outline-none focus:border-tnc-orange uppercase font-mono" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Tên hiển thị (VD: Vi xử lý)</label>
                                        <input type="text" required value={formData.componentName} onChange={e => setFormData({...formData, componentName: e.target.value})} className="w-full border p-2 rounded outline-none focus:border-tnc-orange" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Thứ tự hiển thị (1, 2, 3...)</label>
                                        <input type="number" required min="1" value={formData.sortOrder || ''} onChange={e => setFormData({...formData, sortOrder: e.target.value ? parseInt(e.target.value) : ''})} className="w-full border p-2 rounded outline-none focus:border-tnc-orange" />
                                    </div>
                                    
                                    {/* 👉 ĐÃ THÊM: Ô nhập link Icon ở đây */}
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Icon (Chọn ảnh từ máy)</label>
                                        <div className="flex items-center gap-3">
                                            {/* Khối hiển thị ảnh xem trước */}
                                            <div className="w-12 h-12 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                                                {formData.icon ? (
                                                    <img src={formData.icon} alt="icon preview" className="w-full h-full object-contain p-1" />
                                                ) : (
                                                    <span className="text-[10px] text-gray-400 font-medium">Trống</span>
                                                )}
                                            </div>

                                            {/* Nút chọn file */}
                                            <div className="flex-1">
                                                <input 
                                                    type="file" 
                                                    accept="image/*"
                                                    onChange={handleUploadIcon}
                                                    disabled={isUploadingIcon}
                                                    className="w-full border p-1.5 rounded outline-none focus:border-tnc-orange text-sm
                                                            file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 
                                                            file:text-xs file:font-bold file:bg-orange-50 file:text-tnc-orange 
                                                            hover:file:bg-orange-100 cursor-pointer disabled:opacity-50" 
                                                />
                                                {isUploadingIcon && (
                                                    <p className="text-xs text-blue-500 mt-1 font-medium animate-pulse">
                                                        Đang tải ảnh lên server, vui lòng đợi...
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Input ẩn để lưu URL (hoặc bạn có thể biến thành input text cho phép sửa tay URL nếu thích) */}
                                        {formData.icon && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <span className="text-[10px] text-gray-400">URL:</span>
                                                <input type="text" readOnly value={formData.icon} className="text-[10px] text-gray-500 w-full bg-transparent outline-none truncate" />
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Loại Tag (VD: SOCKET, RAM_TYPE)</label>
                                        <input type="text" required value={formData.groupType} onChange={e => setFormData({...formData, groupType: e.target.value.toUpperCase()})} className="w-full border p-2 rounded outline-none focus:border-tnc-orange uppercase font-mono" placeholder="Nhập không dấu, không cách" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold mb-1">Giá trị (VD: LGA 1700, DDR5)</label>
                                        <input type="text" required value={formData.groupValue} onChange={e => setFormData({...formData, groupValue: e.target.value})} className="w-full border p-2 rounded outline-none focus:border-tnc-orange" />
                                    </div>
                                </>
                            )}
                            <button type="submit" className="w-full bg-tnc-orange text-white font-bold py-2 rounded mt-4 hover:bg-tnc-orange-hover transition-colors">LƯU LẠI</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPcBuilderConfig;